import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

export type ToastVariant = "success" | "danger" | "info" | "warning";
export interface ToastOptions {
  title?: string;
  duration?: number;
}
interface ToastItem {
  id: number;
  message: string;
  variant: ToastVariant;
  title?: string;
}

interface ToastApi {
  success: (message: string, options?: ToastOptions) => void;
  error: (message: string, options?: ToastOptions) => void;
  info: (message: string, options?: ToastOptions) => void;
  warning: (message: string, options?: ToastOptions) => void;
}

const DEFAULT_DURATION = 4500;
const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const sequence = useRef(0);
  const timers = useRef(new Set<number>());
  // Allo smontaggio i timer ancora in coda si annullano: scatterebbero su un provider che non c'e' piu'
  // (nei test, a ambiente jsdom gia' chiuso: "window is not defined").
  useEffect(() => {
    const pending = timers.current;
    return () => {
      pending.forEach((timer) => window.clearTimeout(timer));
      pending.clear();
    };
  }, []);
  const push = useCallback((variant: ToastVariant, message: string, options?: ToastOptions) => {
    const id = ++sequence.current;
    setItems((current) => [...current, { id, message, variant, title: options?.title }]);
    const timer = window.setTimeout(() => {
      timers.current.delete(timer);
      setItems((current) => current.filter((item) => item.id !== id));
    }, options?.duration ?? DEFAULT_DURATION);
    timers.current.add(timer);
  }, []);
  const api = useMemo<ToastApi>(
    () => ({
      success: (message, options) => push("success", message, options),
      error: (message, options) => push("danger", message, options),
      info: (message, options) => push("info", message, options),
      warning: (message, options) => push("warning", message, options),
    }),
    [push],
  );
  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="as-toast-viewport" aria-live="polite">
        {items.map((item) => (
          <div className={`as-toast ${item.variant}`} key={item.id}>
            {item.title && <strong>{item.title}</strong>}
            <span>{item.message}</span>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error("useToast va usato dentro ToastProvider");
  return api;
}
